"use client";

import type * as React from "react";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { Icon, type IconName } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

import { useNotificationPanelLock } from "../../../lib/panel-lock";
import { initialsFor } from "../../../lib/helpers";
import type { NotificationUserMetadata } from "../../../lib/metadata";
import type { Notification } from "../../../lib/types";
import { NotificationMentionPreview } from "./mention-preview";

export interface NotificationItemProps {
  notification: Notification;
}

interface ContainerProps extends React.ComponentProps<"div"> {
  hasTargetUrl: boolean;
  isUnread: boolean;
  onActivate: () => void;
  onMarkRead: () => void;
  onMarkUnread: () => void;
}

function UnreadDot() {
  return (
    <span
      aria-label="Unread"
      className="absolute right-2 top-2 size-2 rounded-full bg-primary"
    />
  );
}

function Container({
  children,
  className,
  hasTargetUrl,
  isUnread,
  onActivate,
  onMarkRead,
  onMarkUnread,
  onClick,
  onKeyDown,
  role,
  tabIndex,
  ...props
}: ContainerProps) {
  const setPanelLocked = useNotificationPanelLock();
  const isInteractive = hasTargetUrl || isUnread;

  function handleClick(event: React.MouseEvent<HTMLDivElement>) {
    onClick?.(event);
    if (!event.defaultPrevented && isInteractive) onActivate();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    onKeyDown?.(event);
    if (event.defaultPrevented || !isInteractive) return;
    if (event.key === "Enter") onActivate();
  }

  return (
    <ContextMenu onOpenChange={setPanelLocked}>
      <ContextMenuTrigger asChild>
        <div
          role={role ?? (isInteractive ? "button" : undefined)}
          tabIndex={tabIndex ?? (isInteractive ? 0 : undefined)}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          className={cn(
            "group relative flex items-start gap-3 rounded-md border border-border bg-surface-primary px-3 py-2.5 dark:bg-surface-inset",
            "shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] transition-[background-color,border-color] duration-150",
            isInteractive &&
              "cursor-pointer hover:border-contrast/15 hover:bg-surface-hover",
            className,
          )}
          {...props}
        >
          {children}
          {isUnread && <UnreadDot />}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        {isUnread ? (
          <ContextMenuItem onSelect={onMarkRead}>
            <Icon name="IconEyeOpen" size={14} />
            Mark as read
          </ContextMenuItem>
        ) : (
          <ContextMenuItem onSelect={onMarkUnread}>
            <Icon name="IconEyeClosed" size={14} />
            Mark as unread
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}

interface MediaProps extends React.ComponentProps<"div"> {
  actor: NotificationUserMetadata | null;
  iconName: IconName;
  iconStyle: string;
}

function Media({
  actor,
  className,
  iconName,
  iconStyle,
  ...props
}: MediaProps) {
  const hasActorTile = Boolean(actor);

  return (
    <div className={cn("relative size-8 shrink-0", className)} {...props}>
      {hasActorTile ? (
        <Avatar className="size-8 rounded-sm border border-border">
          <AvatarImage src={actor?.avatarUrl} alt="" />
          <AvatarFallback className="rounded-sm text-xs font-semibold text-heading">
            {initialsFor(actor?.name ?? "")}
          </AvatarFallback>
        </Avatar>
      ) : (
        <div className="flex size-8 items-center justify-center rounded-sm bg-primary text-primary-foreground">
          <Icon name={iconName} size={16} className="text-current" />
        </div>
      )}
      {hasActorTile && (
        <span
          className={cn(
            "absolute -bottom-0.75 -right-0.75 flex size-4.5 items-center justify-center rounded-[0.3rem] border border-surface-primary dark:border-surface-inset",
            iconStyle,
          )}
        >
          <Icon name={iconName} size={10} className="text-current" />
        </span>
      )}
    </div>
  );
}

function Content({
  children,
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div className={cn("flex min-w-0 flex-1 flex-col", className)} {...props}>
      {children}
    </div>
  );
}

interface TitleProps extends React.ComponentProps<"p"> {
  isUnread: boolean;
}

function Title({ children, className, isUnread, ...props }: TitleProps) {
  return (
    <p
      className={cn(
        "text-xs leading-tight text-pretty text-muted-foreground",
        isUnread && "font-medium text-heading",
        className,
      )}
      {...props}
    >
      {children}
    </p>
  );
}

function Body({ children, className, ...props }: React.ComponentProps<"p">) {
  const shouldRenderMentionPreview = typeof children === "string";

  return (
    <p
      className={cn(
        "text-xs leading-4 mt-1 rounded-sm border border-border bg-surface-inset p-2 text-contrast/60 line-clamp-3 group-hover:line-clamp-none dark:text-contrast/50",
        className,
      )}
      {...props}
    >
      {shouldRenderMentionPreview ? (
        <NotificationMentionPreview content={children} />
      ) : (
        children
      )}
    </p>
  );
}

function Time({ children, className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      className={cn(
        "text-xs leading-4 text-contrast/60 dark:text-contrast/50",
        className,
      )}
      {...props}
    >
      {children}
    </p>
  );
}

export const NotificationItemLayout = {
  Body,
  Container,
  Content,
  Media,
  Time,
  Title,
  UnreadDot,
};
